<template>
  <div class="ad-type-management">
    <div class="page-header">
      <h2>广告类型</h2>
      <el-button type="primary" @click="showCreateDialog">新增类型</el-button>
    </div>

    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="这里的类型就是用户端广告页顶部筛选条上的分类，后台广告列表的类型筛选也用它。"
      description="停用某个类型后，用户端就不再显示这个类型以及它下面的广告；类型下还有广告时不允许删除。"
      class="page-tip"
    />

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="类型名称">
        <el-input v-model="searchForm.name" placeholder="请输入类型名称" clearable @keyup.enter.native="handleSearch" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择" clearable>
          <el-option label="启用" :value="1" />
          <el-option label="停用" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="typeList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column label="图标" width="80" align="center">
        <template slot-scope="scope">
          <span class="type-icon">{{ scope.row.icon || '📎' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="类型名称" min-width="140" show-overflow-tooltip />
      <el-table-column prop="sort_order" label="排序" width="80" />
      <el-table-column prop="status" label="状态" width="90">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini">
            {{ scope.row.status_name }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="ad_count" label="广告数" width="90">
        <template slot-scope="scope">
          <el-link v-if="scope.row.ad_count > 0" type="primary" @click="goToAdList(scope.row)">
            {{ scope.row.ad_count }}
          </el-link>
          <span v-else>0</span>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip />
      <el-table-column prop="create_time" label="创建时间" width="160" />
      <el-table-column label="操作" width="140" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" class="edit-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="560px">
      <el-form ref="typeForm" :model="typeForm" :rules="typeRules" label-width="90px">
        <el-form-item label="类型名称" prop="name">
          <el-input v-model="typeForm.name" placeholder="如：房屋租售" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="类型图标" prop="icon">
          <el-input v-model="typeForm.icon" placeholder="填一个 emoji，如 🏠" maxlength="20" />
          <div class="icon-picker">
            <span
              v-for="item in commonIcons"
              :key="item"
              class="icon-item"
              :class="{ active: typeForm.icon === item }"
              @click="typeForm.icon = item"
            >{{ item }}</span>
          </div>
          <div class="form-tip">图标会显示在用户端广告页的类型筛选条上，不填则用默认图标</div>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="typeForm.sort_order" :min="0" :max="9999" controls-position="right" />
          <span class="form-tip">数字越小越靠前</span>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch
            v-model="typeForm.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="停用"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="typeForm.remark"
            type="textarea"
            :rows="3"
            placeholder="选填，比如这个类型是做什么的"
            maxlength="255"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAdTypeList,
  getAdTypeDetail,
  createAdType,
  updateAdType,
  deleteAdType
} from '@/api/ad'

export default {
  name: 'AdTypeManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      typeList: [],
      // 快捷图标，点一下就填到输入框里
      commonIcons: ['🏠', '🏪', '💼', '🔄', '🛠', '📎', '🍜', '🚗', '🎓', '💰', '🎉', '📢'],
      searchForm: {
        name: '',
        status: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增广告类型',
      typeForm: this.getEmptyForm(),
      typeRules: {
        name: [
          { required: true, message: '请输入类型名称', trigger: 'blur' },
          { max: 50, message: '类型名称不能超过50个字符', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.fetchTypeList()
  },
  methods: {
    getEmptyForm() {
      return {
        id: null,
        name: '',
        icon: '',
        sort_order: 0,
        status: 1,
        remark: ''
      }
    },
    async fetchTypeList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        const response = await getAdTypeList(params)
        this.typeList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } catch (error) {
        console.error('获取广告类型列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchTypeList()
    },
    handleReset() {
      this.searchForm = {
        name: '',
        status: ''
      }
      this.pagination.page = 1
      this.fetchTypeList()
    },
    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchTypeList()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchTypeList()
    },
    showCreateDialog() {
      this.dialogTitle = '新增广告类型'
      this.typeForm = this.getEmptyForm()
      this.dialogVisible = true
    },
    async handleEdit(row) {
      try {
        const response = await getAdTypeDetail(row.id)
        const detail = response.data
        this.typeForm = {
          id: detail.id,
          name: detail.name,
          icon: detail.icon || '',
          sort_order: detail.sort_order,
          status: detail.status,
          remark: detail.remark || ''
        }
        this.dialogTitle = '编辑广告类型'
        this.dialogVisible = true
      } catch (error) {
        console.error('获取广告类型详情失败:', error)
      }
    },
    handleDelete(row) {
      this.$confirm(
        `确认删除广告类型「${row.name}」吗？`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          await deleteAdType(row.id)
          this.$message.success('删除成功')
          this.fetchTypeList()
        } catch (error) {
          console.error('删除广告类型失败:', error)
        }
      })
    },
    handleSubmit() {
      this.$refs.typeForm.validate(async (valid) => {
        if (!valid) {
          return
        }
        this.submitting = true
        try {
          if (this.typeForm.id) {
            await updateAdType(this.typeForm)
            this.$message.success('更新成功')
          } else {
            await createAdType(this.typeForm)
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchTypeList()
        } catch (error) {
          console.error('操作失败:', error)
        } finally {
          this.submitting = false
        }
      })
    },
    // 点广告数跳到广告列表，并按这个类型筛出来
    goToAdList(row) {
      this.$router.push({ path: '/dashboard/ad/list', query: { ad_type: row.type } })
    }
  }
}
</script>

<style scoped>
.ad-type-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.page-tip {
  margin-bottom: 16px;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.type-icon {
  font-size: 22px;
  line-height: 1;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 8px;
  font-size: 12px;
  border: none;
  background: none;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}

.dialog-footer {
  text-align: right;
}

.icon-picker {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.icon-item {
  font-size: 20px;
  line-height: 1;
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  background: #fff;
}

.icon-item:hover {
  border-color: #409eff;
}

.icon-item.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.form-tip {
  color: #909399;
  font-size: 12px;
  margin-left: 10px;
}
</style>
